import React from "react";
import styled, { keyframes } from "styled-components";
import ScrollArea from "src/components/ScrollArea";
import PieRing, { RING_MARGIN } from "src/components/charts/PieRing";
import { ArrowRight01Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { theme } from "src/theme/theme";
import { line, radii, space, text } from "src/theme/ui";
import {
  hatchFill,
  isHatchedType,
  typeColorFor,
} from "src/theme/positionTypes";

/**
 * A colour per holding.
 *
 * This was shades of the one accent, which is the quieter choice and the
 * wrong one here: a ramp of fourteen closely-spaced blues cannot separate
 * more than about three slices — measured, its best four-shade set sits at
 * 92 on a combined RGB-and-luminance scale and its best five at 78 — so a
 * list of five holdings came out as one colour changing its mind. Telling
 * holdings apart is the job, so the palette is categorical: distinct hues,
 * in a fixed order, at a single lightness and chroma discipline so they
 * still read as one system.
 *
 * The order is VALIDATED rather than chosen by eye, with the validator in
 * the dataviz skill. On the adjacent pairlist — which is what a ring's
 * neighbouring arcs and a bar list are — every hard gate passes: worst
 * adjacent colour-blind separation ΔE 9.2 against a target of 8, worst
 * normal-vision ΔE 19.6 against a floor of 15, every hue inside the
 * lightness band and above the chroma floor. Magenta, yellow and aqua sit
 * under 3:1 against a white card, which obliges the relief rule — visible
 * labels rather than colour alone — and both views carry one: the ring
 * names every slice with its amount and share, the bar view labels each row.
 *
 * The first FOUR also clear the stricter all-pairs floors (worst ΔE 16.3
 * colour-blind, 18.1 normal), which is the honest limit: past four, two
 * slices somewhere in the list are close enough that the legend label is
 * doing the work. That is a real ceiling, not a preference — reordering
 * cannot fix it, only fewer series or a fold into "Other" can.
 *
 * Red and green are deliberately absent. In this product they mean a loss
 * and a gain, and a holding that merely happens to be sixth in a list must
 * not be painted as though it were down.
 *
 * THE ORDER IS THE PALETTE. Blue then orange then magenta then teal, which
 * is not the order they were in: the second hue was a light pink, and the
 * second hue is the one that appears in every composition of two — most of
 * them. Re-stepped and re-ordered against the six checks (OKLab separation,
 * each of the three dichromacies simulated, the lightness band, the chroma
 * floor, contrast on white), every ADJACENT pair now passes, where the old
 * order had the pink failing against the yellow under tritanopia at a
 * separation of 6.
 *
 * Two things had to move to make room for the orange. The pink went to a
 * deeper magenta, because a light pink cannot be told from a green under
 * either red-green deficiency. The cyan went to a teal, because cyan sits
 * 12 from the accent blue in normal vision — two arcs in the same ring, one
 * colour to a reader who is not looking closely.
 *
 * FIVE hues, down from six. With the accent fixed at blue and red and green
 * spoken for, a sixth hue has to sit within touching distance of one of the
 * five, and a colour that says "not the fifth thing" while looking like it
 * is worse than a neutral that says "and the rest" — which is also how the
 * data itself is grouped, since a long tail arrives as one "Others" row.
 *
 * What could not be solved: orange lives in the gap between the loss red
 * (#F04438) and the warning amber (#F79009), about 6 and 10 away, and no
 * orange in the whole band escapes both — the best available manages 8.
 * It is a composition here, so an arc carries no direction and cannot be
 * read as a fall; the two are never side by side, since the status colours
 * appear as text and pills and never as an arc or a legend dot.
 */
export const chartColors = [
  /* The tenant's own accent leads, so the largest holding stays on brand.
     On this tenant that is a blue. */
  theme.colors.primary[500],
  "#EB6834", // orange
  "#B83280", // magenta
  "#12B0A6", // teal
  "#EDA100", // yellow
  /* Past the validated hues, neutrals rather than invented colours. */
  theme.colors.gray[500],
  theme.colors.gray[400],
];

/* What each kind of position wears — cash, brokerage, private equity and the
   dashboard's groups — and which of them are hatched lives in
   theme/positionTypes, so the KPI strips, this ring and the bar view all
   draw a kind the same way. */

/**
 * What an empty holding wears where it is still listed — the bar view.
 *
 * Near-white rather than a mid grey: the bar has no width to speak of, so
 * this is really the colour of its legend dot, and it should read as nothing
 * held rather than as one more holding.
 */
export const emptyColor = line.soft;

/**
 * A colour per holding, keyed by its name rather than by its position.
 *
 * The ring drops a holding worth nothing before it draws — an empty slice has
 * no arc, only a stray stroke — while the bar view of the same data keeps
 * every row. Indexing the ramp by position therefore handed the two views
 * different colours for the same holding as soon as a zero row sat between
 * two others: the ring read the ramp's second colour where the bars read its
 * third.
 *
 * Both views build their lookup from this, off the FULL list in the order it
 * arrives, so they cannot drift — and a holding keeps its colour when a
 * filter changes what is on screen.
 */
export const colorMapFor = (rows: { x: string; y?: number }[]) => {
  const map: Record<string, string> = {};
  /* A colour a kind on this list already wears is not handed to a holding
     as well: with private equity in orange, the next holding along the
     rotation would otherwise be a second orange. */
  const taken = new Set(
    (rows ?? []).map((row) => typeColorFor(row.x)).filter(Boolean),
  );
  const ramp = chartColors.filter((color) => !taken.has(color));
  let step = 0;
  (rows ?? []).forEach((row) => {
    if (row.x in map) return;
    /* A kind keeps its own colour in every grouping, and does not spend a
       step of the invested ramp. */
    const byType = typeColorFor(row.x);
    if (byType) {
      /* A kind with nothing in it reads as nothing held, like any holding. */
      map[row.x] = (row.y ?? 0) > 0 ? byType : emptyColor;
      return;
    }
    /* A holding worth nothing does not spend a step of the ramp. It draws no
       arc at all in the ring, and burning a colour on it pushed the holdings
       that DO draw further apart in the list than they are on screen: with a
       zero row between them, two visible slices took steps 1 and 3 and came
       out 49 apart on the measure below — the low contrast that reads as one
       colour changing its mind. Spending steps only on what is drawn puts
       them on consecutive steps, which is what the order is built for. */
    if ((row.y ?? 0) > 0) {
      map[row.x] = ramp[step % ramp.length];
      step += 1;
    } else {
      map[row.x] = emptyColor;
    }
  });
  return (label: string) => map[label] ?? ramp[0];
};

/* Ring and legend side by side while there is room for both, and the legend
   wrapping underneath when there is not. Flex decides that from the space it
   is given, so there is no width to measure and no breakpoint to pick — which
   is what the previous chart got wrong: it kept the choice as internal state,
   settled it a frame late, and the two halves could disagree. */
const Wrap = styled.div<{ $vertical?: boolean }>`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  /**
   * The two lines stay TOGETHER when the legend wraps under the ring.
   *
   * Wrapping makes this a two-line flex container, and the default
   * align-content is stretch — so the lines spread to fill the card and the
   * gap between the ring and the list grew to whatever the row had spare.
   * The pair is one object; it centres as one, with the row-gap between its
   * halves and the slack outside it rather than through the middle.
   */
  align-content: center;

  /**
   * The ring OVER the list, when the card asks for it.
   *
   * Not the same thing as the wrap that happens when a card is too narrow
   * for both on one line: that is a fallback and this is a choice, so it
   * does not depend on the width and cannot flip as the page resizes. The
   * pair centres as one object and the list is bounded, so a wide card does
   * not stretch three rows across the whole of it.
   */
  ${({ $vertical }) =>
    $vertical
      ? `
        flex-direction: column;
        flex-wrap: nowrap;
        /**
         * The ring is PINNED to the top, and the list takes what is left.
         *
         * Centring the pair looked right in one card and wrong in a row of
         * them: a card of three rows and a card of five centre blocks of
         * different heights, so their rings came to rest at different
         * heights and the row read as two charts nudged out of line. The
         * ring is the biggest object on these cards and the first thing the
         * eye lands on, so it is the thing that has to agree across the
         * row.
         *
         * The surplus does not pile up under the list, either: the list
         * FILLS the space below the ring and centres its rows inside it
         * (see ScrollArea's own centring), so what is left over is split
         * above and below the rows rather than dumped at the card's floor.
         */
        justify-content: flex-start;
        align-items: center;
      `
      : ""}
  /* Spread across whatever width the card has. Centred, the pair clustered
     in the middle and left the rest of a wide card empty — and there is a
     limit to the spreading, just below. */
  justify-content: space-evenly;

  /**
   * The spread has a limit, past which the pair centres instead.
   *
   * Spreading evenly is right while the card is close to the pair's own
   * size: it lets the ring and the list breathe apart rather than clumping
   * in the middle of a 500px card. It stops being right when the card is
   * much bigger than they are — a 188px ring and a legend capped at 320
   * cannot fill 1100px, and evenly spread across that they end up at
   * opposite ends of the card with a void between them, which reads as two
   * cards that lost their divider. 560 is the pair plus a little air; past
   * it the group holds together and the card's extra width becomes margin.
   *
   * Done with padding rather than a max-width and auto margins. This box is
   * a flex item in the card's column, so auto margins on the cross axis turn
   * OFF the stretch that gives it a width at all — it shrank to fit its own
   * contents instead, came out at 426px in a 655px card, and the legend
   * wrapped under the ring for want of the room the card actually had.
   */
  padding-left: max(0px, calc((100% - 640px) / 2));
  padding-right: max(0px, calc((100% - 640px) / 2));
  gap: ${space.md}px ${space.lg}px;
  min-height: 0;
`;

/* Square, so the ring has a height derived from its width rather than one
   handed to it in pixels. Capped so it cannot outgrow a short card, and held
   back from filling the card completely, which reads as an oversized ring
   rather than a well-fitted one. */
const RingBox = styled.div<{
  $compact?: boolean;
  /** The legend has wrapped under the ring rather than sitting beside it. */
  $wrapped?: boolean;
  /** Stacked over the legend by choice — see Wrap. */
  $vertical?: boolean;
  /**
   * The host's chosen edge length when the ring is over the list.
   *
   * The host is the one that knows how tall its card is, and over the list
   * the ring's height is what it costs that card. A percentage cap was the
   * first answer and it cannot work: a max-height in per cent needs a
   * definite height on the parent to resolve against, and the parent is a
   * flex child — it silently resolved to nothing and the ring kept its
   * full size while the list was left with none.
   */
  $size?: number;
}>`
  position: relative;
  /* A step down from 190/230. At the old size the ring was the biggest object
     on the page and the card was mostly ring; the figure in the middle is
     what the card is for, and the ring is the proportion around it.

     Smaller again when the middle is EMPTY. The ring is sized to hold the
     total in its hole; with no total to hold, that hole is just white space,
     and a 188px ring around nothing is the largest object on the card for no
     reason. The arcs stay the same thickness, so nothing gets harder to
     read — the empty middle is what shrinks. */
  /**
   * The basis is the ring's MINIMUM, not its preferred size.
   *
   * Flex wraps on the hypothetical main size — the basis — not on
   * min-width, so a 180px basis beside the legend's 272 asked for 484px of
   * line and the pair broke onto two lines in a 492px card that could have
   * held them at 442. The ring grows into whatever is left after the legend
   * has its floor; what it must not do is claim room it can give back.
   */
  /**
   * In a COLUMN the basis is a height, so the ring is given a size instead.
   *
   * Side by side, the basis is the ring's minimum width and it grows into
   * whatever the legend leaves. Stacked, the main axis is vertical: the same
   * 138px basis became the ring's HEIGHT, and the aspect ratio then made it
   * 138 wide — a ring a third smaller than intended, for a reason that has
   * nothing to do with how much room the card had.
   */
  ${({ $vertical, $compact, $size }) =>
    $vertical
      ? `
        flex: none;
        width: auto;
        height: ${$size ?? ($compact ? 160 : 184)}px;
        /* Slightly off the head. With the list below it set to grow, the
           ring is pushed hard against the top of the body — level with the
           card's title, which reads as the two of them sharing a line. Ten
           pixels is enough to separate them and comes out of the list's
           share, which has four rows either way. */
        margin-top: 10px;
      `
      : `
        flex: ${$compact ? "1 1 112px" : "1 1 138px"};
      `}
  min-width: ${({ $compact }) => ($compact ? 112 : 138)}px;
  /**
   * A different ceiling when the legend has WRAPPED under the ring.
   *
   * Beside the legend, the ring's height costs nothing: the row is as tall
   * as the taller of the two, and the aspect ratio with a 100% max-height
   * already holds the ring to the height it is given. Wrapped, the ring's
   * height ADDS to the legend's — so the same cap that is free in one
   * layout is what makes the card tall in the other. Raising it to 288
   * without that distinction put a 288px ring above a 204px legend and asked
   * for a 574px row, which the two cards beside it then had to match.
   *
   * So: 160 when the legend has WRAPPED under it, and 200 when it is beside
   * it — where the row height is the real limit anyway.
   */
  max-width: ${({ $compact, $wrapped }) =>
    $compact ? 168 : $wrapped ? 160 : 200}px;
  aspect-ratio: 1;
  max-height: 100%;
  /* Floor for anything that does not honour aspect-ratio: without a height
     from somewhere the chart falls back to its own default of 400px. */
  min-height: ${({ $compact }) => ($compact ? 112 : 138)}px;
`;

/**
 * The ring with nothing in it.
 *
 * A holding worth nothing draws no arc — correctly, an arc of zero degrees is
 * not a thing — so a group whose every row is zero drew NO ring at all: a
 * figure floating in white space, which reads as a chart that failed to load
 * rather than as an account with nothing in it. This is the band's own track,
 * in the grey the app draws structure in, so the shape is there and visibly
 * inert.
 *
 * A gradient rather than a border, because the band's thickness is a fraction
 * of a radius the box works out from its own width — `closest-side` on a
 * square is exactly that radius, so the hole stays at `inner` whatever size
 * the card gives the ring.
 */
const Track = styled.div<{ $inner: number }>`
  position: absolute;
  /* The same reserve the ring keeps for its hover, so the empty state and
     the drawn one are the same size — the band does not jump a few pixels
     wider the moment the account has something in it. */
  inset: ${RING_MARGIN}px;
  border-radius: 50%;
  background: radial-gradient(
    closest-side,
    transparent 0 ${({ $inner }) => ($inner * 100).toFixed(1)}%,
    ${colors.gray[200]} ${({ $inner }) => ($inner * 100).toFixed(1)}% 100%
  );
  pointer-events: none;
`;

/**
 * A dial's tick marks, on a smaller circle inside the arcs.
 *
 * Sixty fine radial ticks on a ring of their own, clear of the band with a
 * visible gap. It is the one piece of decoration in this chart and it earns
 * its place: a ring of rounded arcs on empty white has no scale, so a slice
 * reads as a shape and not as a proportion of anything. Ticks give the eye
 * something to measure the arcs against — the same reason a dial has them
 * and a pie chart does not.
 *
 * The first attempt drew them from just inside the hole all the way out to
 * the arcs, which put a hatched band UNDER the band and read as texture on
 * the ring rather than as a scale beside it. The geometry is stated in terms
 * of the hole now: the ticks' outer edge sits seven points of the radius
 * inside the arcs, and the marks themselves are seven points long, so the
 * whole scale is a thin dotted circle with air on both sides of it.
 *
 * The marks grow INWARD — their outer edge is what the gap is measured
 * from, and the mask cuts the inner end — so making them longer takes room
 * from the hole rather than from the air between the scale and the band.
 * At 5 points they were 4.4px on the standard ring, short enough to read as
 * a dotted line; 7 points is 6.2px, which reads as a mark with a direction.
 *
 * `repeating-conic-gradient` draws the marks and a radial mask cuts them
 * back to that ring.
 */
const TICK_GAP = 0.07;
const TICK_LENGTH = 0.07;

/* A circle of radius k*R, centred in a square box of side 2R, is inset by
   50*(1-k) per cent of the box. */
const insetFor = (k: number) => `${(50 * (1 - k)).toFixed(2)}%`;

const Ticks = styled.div<{ $inner: number }>`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  /**
   * Inset in two terms, because the box and the ring are no longer the same
   * circle.
   *
   * The per-cent places the scale against the BOX; the pixels take off what
   * the ring reserves for its hover. Both are needed: the arcs are drawn at
   * a radius of R minus that reserve, so a scale positioned against R alone
   * drifted outward as the ring shrank — the gap between the ticks and the
   * band closed from seven points of the radius to barely one, and the
   * scale read as the band's inner edge rather than as a separate ring.
   *
   * The pixel term is scaled by where the scale sits, so the whole geometry
   * shrinks by the same ratio the arcs do, at every ring size this draws
   * (112, 150, 188 and 224px) without a breakpoint.
   */
  inset: ${({ $inner }) =>
    `calc(${insetFor($inner - TICK_GAP)} + ${(
      RING_MARGIN *
      ($inner - TICK_GAP)
    ).toFixed(2)}px)`};
  /**
   * Sixty marks, each about a pixel wide.
   *
   * A conic gradient's stops are ANGLES, so a mark's rendered width depends
   * on the radius it is drawn at: 0.3 degrees at this ring's 71px works out
   * to 0.38px, which is under one device pixel and disappeared entirely.
   * 0.9 degrees lands at 1.1px — the thinnest a line can be and still be
   * drawn — and every sixth degree keeps sixty of them.
   *
   * Gray 300 rather than 200: a one-pixel line is antialiased across two
   * device pixels, so half its ink is spent before it reaches the eye. The
   * hairline greys are calibrated for a solid 1px edge; a dotted mark this
   * fine needs the next step down to read at all.
   */
  background: repeating-conic-gradient(
    from 0deg,
    ${colors.gray[300]} 0deg 0.9deg,
    transparent 0.9deg 6deg
  );
  /* The marks' inner end, as a fraction of THIS box rather than of the
     ring: a percentage in a radial-gradient is read against the element it
     paints, and this element is already smaller than the ring. */
  --tick-inner: ${({ $inner }) => {
    const outer = $inner - TICK_GAP;
    return `${(((outer - TICK_LENGTH) / outer) * 100).toFixed(1)}%`;
  }};
  mask: radial-gradient(
    closest-side,
    transparent 0 var(--tick-inner),
    #000 var(--tick-inner) 100%
  );
  -webkit-mask: radial-gradient(
    closest-side,
    transparent 0 var(--tick-inner),
    #000 var(--tick-inner) 100%
  );
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/* Decoration over the middle of the ring: it must not take the pointer, or
   the slices underneath never show their tooltip. */
const Center = styled.div`
  animation: ${fadeIn} 220ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  pointer-events: none;
  text-align: center;
`;

const CenterCaption = styled.span`
  ${text.label}
`;

const CenterAmount = styled.span`
  ${text.numeric}
  font-size: 19px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: ${colors.gray[900]};
`;

/**
 * The legend's own box, and the ONLY thing in this card that scrolls.
 *
 * The list used to scroll itself with a bare overflow, inside a card whose
 * whole body was ALSO a scroll pane — so a card with eight sectors had two
 * nested scrollers and a wheel over the legend moved whichever one the
 * browser felt like. Worse, the outer one scrolled the RING out of the card:
 * the ring is not a list, it does not continue below the fold, and pushing
 * it off the top to reach a legend row loses the very thing the row names.
 *
 * So the ring is fixed and the list scrolls under it, with the scroll area's
 * fades and its cue to say so.
 */
const LegendBox = styled.div<{
  $fill?: boolean;
  $floor: number;
  $vertical?: boolean;
}>`
  /* Block by default: the pane inside is a plain div and should take the
     box's whole width. As a flex parent with a fixed-height pane it sized
     itself from the list's own min-content instead, which pushed the ring
     onto a line of its own — the pane has to be able to GROW for the flex
     box to hand it a width.

     In fill mode it is a flex column and the pane grows, so both hold. */
  ${({ $fill }) =>
    $fill ? "display: flex; align-self: stretch; min-height: 0;" : ""}
  /* A floor, not zero, and one COMPUTED from the row's own columns rather
     than written beside them — see legendFloor above. At zero the list
     shrinks to a sliver rather than wrapping, because a flex item that can
     keep shrinking never triggers a wrap; at a stale number it does the
     same thing more slowly. The basis is the floor too, so the list asks
     for what it needs and grows from there. */
  /**
   * Stacked, the list spans the card; beside the ring, it shares the line.
   *
   * The basis is a WIDTH on one axis and a HEIGHT on the other, which is the
   * same trap the ring fell into: in a column, a 272px basis made 272
   * the box's height and left its width to shrink-wrap its own content, so
   * three rows sat in a 265px huddle in the middle of a 413px card with
   * about 75px of unused paper down each side. A full-width child of a
   * centred column needs an explicit width.
   */
  ${({ $vertical, $floor, $fill }) =>
    $vertical
      ? `
        /**
         * FILLING grows into whatever the ring leaves; hugging does not.
         *
         * Filling is for a card whose height is set by something else — the
         * dashboard's grid — where the list showing a hardcoded three rows
         * was a number rather than a measurement: it stayed three when the
         * card got taller and would have stayed three if the ring shrank.
         * Filling means the count is whatever the space holds and the
         * scroll picks up where the space runs out.
         *
         * Hugging is for a card that ASKED for its height and then had to
         * match a taller neighbour: there the list must not swallow the
         * difference, or the ring sits at the top of the card with the rows
         * adrift somewhere below it. At its own size, the ring and the list
         * are one object that the wrap centres — see justify-content
         * there — so the surplus is split above and below the pair instead
         * of appearing under it.
         */
        flex: ${$fill ? "1 1 auto" : "none"};
        min-height: 0;
        display: flex;
        /* An explicit width, NOT align-self: stretch — the auto side
           margins below are what centre the box once its max-width bites,
           and an auto margin on the cross axis switches stretch off. That
           left the box shrink-wrapping its content again, which is the
           thing being fixed. */
        width: 100%;
      `
      : `
        flex: 1 1 ${$floor}px;
        min-width: ${$floor}px;
      `}
  /* Wider when the list is UNDER the ring rather than beside it: there the
     card's whole width is available, and 320 left the three rows huddled in
     the middle of it. Still bounded — a row is a name, a share and an
     amount, and stretching that across 700px puts the amount a page away
     from the name it belongs to. */
  max-width: ${({ $vertical }) => ($vertical ? 480 : 320)}px;
  margin: ${({ $vertical }) => ($vertical ? "0 auto" : "0")};
`;

const Legend = styled.ul<{ $fill?: boolean }>`
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;

  /* In fill mode the list has the card's whole height to sit in, so it sits
     in the MIDDLE of it rather than hugging the top: five rows beside a
     188px ring otherwise left the air where the reader's eye goes next. It
     still scrolls once the rows genuinely outgrow the box. */
  ${({ $fill }) =>
    $fill
      ? `
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: 100%;
      `
      : ""}
`;

/**
 * The legend row's fixed furniture, in px.
 *
 * These are the widths the row cannot give up: the colour chip, the two
 * right-hand columns, the chevron, the gaps between them and the row's own
 * padding. They are named here because the legend's FLOOR is derived from
 * them a few declarations below — and the last time that floor was a
 * hand-written number, a third column was added to the row and the number
 * stayed where it was. The result was measurable: at a 453px card the name
 * column came out THIRTEEN pixels wide and set "Public Markets" one letter
 * to a line, because a flex item that can keep shrinking never triggers the
 * wrap that would have put the list under the ring instead.
 */
const DOT_SIZE = 10;
const SHARE_WIDTH = 52;
const AMOUNT_WIDTH = 76;
const CHEVRON_WIDTH = 14;
const ROW_GAP = space.sm;
const ROW_PADDING = 6;

/**
 * What a name needs to stay a name.
 *
 * Two words of a holding's name set on two lines at 12px, which this row
 * already allows for — the name is the part that gives way, because an
 * amount broken or clipped would be worse than a name on two lines.
 */
const NAME_FLOOR = 76;

/**
 * How many holdings the list shows before it scrolls: whatever fits.
 *
 * There is no number here on purpose. It was three rows times a 41px row,
 * and a constant cannot answer a question about space — it stayed three
 * when the card grew and would have stayed three if the ring shrank. The
 * list fills what the ring leaves and scrolls past it, so the count is a
 * consequence of the geometry and moves with it. The full inventory is one
 * click away in the table below either way.
 */

/* The narrowest the list can be and still read. Below it the legend WRAPS,
   under the ring, where it has the card's whole width. */
const legendFloor = (amount: boolean, chevron: boolean) => {
  const columns = 3 + (amount ? 1 : 0) + (chevron ? 1 : 0);
  return (
    DOT_SIZE +
    NAME_FLOOR +
    SHARE_WIDTH +
    (amount ? AMOUNT_WIDTH : 0) +
    (chevron ? CHEVRON_WIDTH : 0) +
    ROW_GAP * (columns - 1) +
    ROW_PADDING * 2
  );
};

/**
 * The entrance a row plays when the LIST changes.
 *
 * Only when it changes: the legend is keyed on its names, so a poll that
 * refreshes the amounts leaves the key alone and nothing moves. What does
 * change the key is drilling into a group, and that is a reader's own
 * gesture — the one moment a list has earned the right to arrive rather
 * than simply be there.
 *
 * 6px and 260ms on the same easing curve the segmented control's thumb
 * uses, staggered 22ms a row. The stagger is what makes it read as a list
 * being dealt rather than a block being faded: eight rows finish 154ms
 * apart, which is under the threshold where a reader starts waiting for the
 * last one.
 */
const dealIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const Row = styled.li<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${ROW_GAP}px;
  padding: 8px ${ROW_PADDING}px;
  border-radius: ${radii.sm}px;
  transition: background 0.15s ease, transform 0.12s ease;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  animation: ${dealIn} 260ms cubic-bezier(0.22, 1, 0.36, 1) both;

  /* The press itself, on a row that opens onto something: a hair down and
     back, so the click has a floor. */
  ${({ $clickable }) =>
    $clickable
      ? `
        &:active {
          transform: scale(0.995);
        }
      `
      : ""}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;

    &:active {
      transform: none;
    }
  }

  /* A dotted rule between rows, and none under the last. Dotted rather than
     solid: these rows are one list, and a solid hairline between every pair
     divides a list into as many pieces as it has entries. Dots read as the
     ruled paper a column of figures is set on. */
  & + & {
    border-top: 1px dotted ${colors.gray[200]};
  }

  &:hover {
    background: ${line.soft};
  }

  /* The row is the hit area, so the chevron only has to say so. It is always
     in the layout — appearing on hover would move the amount beside it, and
     a target that shifts under a still pointer is a target you miss. */
  &:focus-visible {
    outline: 2px solid ${colors.primary[300]};
    outline-offset: 1px;
  }
`;

/* Says the row opens onto something, in the grey the shares are set in: an
   affordance, not a second piece of information. */
const Chevron = styled.span`
  flex: none;
  display: inline-flex;
  justify-content: center;
  /* The SLOT is always this wide, whether or not there is a glyph in it —
     see the note where it is rendered. */
  width: ${CHEVRON_WIDTH}px;
  margin-left: -2px;
  color: ${colors.gray[400]};
  transition: color 0.15s ease, transform 0.15s ease;

  ${Row}:hover & {
    color: ${colors.gray[600]};
    transform: translateX(1px);
  }
`;

/* A holding worth nothing wears the same colour here as it does in the bar
   view, and that colour is near-white — right for a bar with no width, and
   invisible as a 7px dot on a white card. So an empty holding's dot is drawn
   as an outline: still its own colour, with a hairline round it, which also
   says "nothing held" at a glance rather than relying on the 0.0% beside
   it. */
/**
 * The colour, as a rounded square rather than a dot.
 *
 * A 7px circle beside 12px type is a speck — it reads as a bullet before it
 * reads as a colour, which is the one job it has. Ten pixels on a 3px corner
 * gives the hue enough area to be identified against its arc, and squares off
 * a column of them so the names start on one edge.
 *
 * 3px is deliberately off the rounded scale, which starts at 5px: on a 10px
 * square a 5px corner is a circle again, and DESIGN.md's Shapes rule takes a
 * corner from the element's own height. Same exception the password meter's
 * 6px bar makes.
 */
/* A hatched kind's swatch carries its arc's hatch, so the row and the arc
   read as the same thing rather than as a flat square beside a texture. */
const Dot = styled.span<{
  $color: string;
  $empty?: boolean;
  $hatched?: boolean;
}>`
  flex: none;
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border-radius: 3px;
  background: ${({ $color, $hatched }) =>
    $hatched ? hatchFill($color) : $color};
  ${({ $empty }) =>
    $empty ? `box-shadow: inset 0 0 0 1px ${colors.gray[300]};` : ""}
`;

/* Name over its share, the way the reference sets a list row: the thing you
   are looking for on top, the detail under it in a quieter size. The name
   gives up its width first, because an amount that wrapped or was clipped
   would be worse than a name broken over two lines. */
const Names = styled.div`
  flex: 1;
  min-width: 0;
`;

/**
 * The share, as its own column.
 *
 * It used to sit UNDER the name whenever there was an amount at the end of
 * the row — a second line of 11px grey under every entry, so a five-holding
 * list was ten lines of type and the shares could only be compared by
 * hopping down a zig-zag. Three columns instead: the name reads left, the
 * share and the amount each line up in a column of their own, and the eye
 * can run down either one.
 */
const ShareColumn = styled.span`
  flex: none;
  ${text.numeric}
  min-width: ${SHARE_WIDTH}px;
  font-size: 12.5px;
  font-weight: 600;
  text-align: right;
  color: ${colors.gray[900]};
`;

const Name = styled.div`
  ${text.legend}
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: ${colors.gray[700]};
  overflow-wrap: break-word;
`;

/* The value trails, quieter than the share beside it: on a composition the
   share is the reading and the amount is what it is a share OF. */
const Amount = styled.span`
  flex: none;
  ${text.numeric}
  min-width: ${AMOUNT_WIDTH}px;
  font-size: 12.5px;
  font-weight: 500;
  text-align: right;
  white-space: nowrap;
  color: ${colors.gray[500]};
`;

type Slice = { x: string; y: number };

interface Props {
  data: Slice[];
  symbol?: string;
  /** Caption above the amount in the middle of the ring. */
  subTitle?: React.ReactNode;
  /** Print something in the middle of the ring. */
  showTotal?: boolean;
  /**
   * What the middle says, when the SUM is not worth saying.
   *
   * The centre of a ring holds the whole its arcs divide — a portfolio's
   * total, on the dashboard. A ring of WEIGHTS has no such figure: they sum
   * to 100% by construction, and printing that in the card's most prominent
   * spot says nothing. The closest honest analogue is how many parts the
   * whole is divided across, which is also the one fact the drawing
   * withholds: the shares are legible from the arcs and again in the
   * legend, but the count has to be counted.
   */
  centerValue?: React.ReactNode;
  /** Size of the hole, as a fraction of the radius. */
  inner?: number;
  /** Trails the figure — "%" for a ring of weights rather than amounts. */
  suffix?: string;
  /**
   * Whether the legend prints the value beside each name.
   *
   * Off for a ring of WEIGHTS: the value and the share are then the same
   * number, so the row said "66.0%" under the name and "66%" at the end of
   * the same line.
   */
  showAmount?: boolean;
  /** The ring's widest, for a card that cannot give it 188px. */
  ringSize?: number;
  /**
   * How tall the legend may grow before it scrolls.
   *
   * The caller's, because the caller knows how tall the card is: capped at a
   * constant, a legend of five rows in a 300px card left a band of blank
   * paper above and below it while its own list was cut off at the fifth.
   */
  legendMaxHeight?: number;
  /**
   * Let the legend take the card's full height instead of capping.
   *
   * For a card whose height is settled by something else — a sibling in its
   * row, or a fixed body — where a capped list stops short and leaves the
   * rest of the card as blank paper.
   *
   * Left unset it is DERIVED, because whether filling is safe is a fact
   * about the layout rather than about the caller: see `beside` below.
   * Pass it only to force the question.
   */
  legendFill?: boolean;
  /**
   * Stack the ring over the list instead of setting them side by side.
   *
   * A choice the card makes, not a width the page happens to have.
   */
  vertical?: boolean;
  /**
   * The dial's tick scale inside the arcs.
   *
   * On by default and asked for by ONE card: the dashboard's, where the
   * ring is the first figure on the page and the marks give the eye
   * something to measure an arc against. Everywhere else the ring sits
   * over a list that already prints every share as a number, so the scale
   * is answering a question the card has answered on the line below —
   * decoration in a card whose job is a table with a picture on top.
   */
  ticks?: boolean;

  /**
   * Opens a slice.
   *
   * With this the legend rows and the arcs become controls: the card they
   * sit in swaps its own contents for what is inside the slice. Without it
   * the ring is a picture of a total, as it is everywhere else.
   */
  onSelect?: (label: string) => void;
}

/* Two decimal places, always. These are account balances, and a balance
   rounded to the pound is a balance the reader cannot reconcile against a
   statement — three rows rounded to units summed to a total that was up to
   a unit and a half away from the figure in the middle of the ring. Always
   two, not "when non-zero": a column of amounts where some rows carry
   decimals and others do not cannot be scanned down. */
const money = (value: number, symbol: string, suffix = "") =>
  `${symbol}${(Number.isFinite(value) ? value : 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}${suffix}`;

/**
 * A total split into its parts: a ring, and a list naming each part with its
 * amount.
 *
 * The ring is drawn by the same charting library as the rest of the page,
 * and the list is ours, so the layout is decided in CSS rather than inside a
 * component that also holds the chart.
 */
const Donut: React.FC<Props> = ({
  data,
  symbol = "$",
  suffix = "",
  showAmount = true,
  ringSize,
  legendMaxHeight,
  legendFill,
  vertical,
  ticks = true,
  onSelect,
  subTitle = "Total",
  showTotal,
  centerValue,
  /**
   * A slim ring reads as a chart rather than a pie chart, and leaves the
   * middle free for the total. At a 94px radius that leaves about 14px of
   * band — thin enough to read as a chart, and still wide enough that the
   * 8px corner below clamps to a fully rounded end rather than a chamfer.
   *
   * A ring with NO total in it takes a bolder band instead: the hole was only
   * ever there to hold the figure, and a 14px band around 150px of white
   * paper is a hairline drawn around nothing. At 0.58 the band was half the
   * radius and the ring read as a pie with a hole punched in it; 0.68 leaves
   * about 36px of band on a 224px ring — still deep enough to carry the
   * share inside the arc, and now a ring rather than a wheel.
   */
  inner,
}) => {
  /**
   * Whether the list is sitting BESIDE the ring or has wrapped under it.
   *
   * The two cases want opposite things from the legend, which is why this is
   * measured rather than passed. Beside the ring, the card's height is
   * settled by something else — the row, or a taller sibling — so a list
   * capped at a constant stops short and leaves the rest of the card as
   * blank paper: it should FILL. Wrapped under the ring, the card has no
   * height of its own but the one its contents ask for, and a list told to
   * fill an unbounded line ran 145px past the card's edge: it should CAP.
   *
   * Flex decides which of the two happened from the width it is given, so
   * the only honest way to know is to ask the boxes where they ended up —
   * and the test for that is whether they OVERLAP vertically. Comparing
   * their tops does not work: the row centres its items, so a 188px ring
   * beside a 260px list starts 36px lower than the list does while plainly
   * on the same line. Comparing their lefts does not work either, because a
   * wrapped line is centred and the two boxes are different widths, so they
   * get different lefts there too. Overlap is the thing being asked about.
   */
  const ringBox = React.useRef<HTMLDivElement>(null);
  const legendBox = React.useRef<HTMLDivElement>(null);
  const [beside, setBeside] = React.useState(false);

  React.useEffect(() => {
    const ring = ringBox.current;
    const legend = legendBox.current;
    if (!ring || !legend) return undefined;

    const measure = () =>
      setBeside(
        ring.offsetTop < legend.offsetTop + legend.offsetHeight &&
          legend.offsetTop < ring.offsetTop + ring.offsetHeight,
      );

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(ring);
    observer.observe(legend);
    return () => observer.disconnect();
    /* The boxes are only in the tree with something to list, and the
       arrangement decides whether they can be side by side at all, so the
       subscription is rebuilt when either changes. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical, (data ?? []).length]);

  const rows = data ?? [];
  const slices = rows.filter((slice) => (slice?.y ?? 0) > 0);
  const total = slices.reduce((sum, slice) => sum + (slice?.y ?? 0), 0);

  /* Bold when the middle is empty, slim when it holds the total. */
  /**
   * A thin band.
   *
   * 0.68 made the arcs almost half the radius, which on a three-slice
   * composition reads as a heavy wheel rather than as a ring — the shape is
   * supposed to be a thin line whose LENGTHS are the reading, and a fat arc
   * turns each slice into a block. 0.82 without a figure in the middle, and
   * the 0.85 that already held with one.
   */
  const hole = inner ?? (showTotal ? 0.85 : 0.82);

  const colorOf = React.useMemo(() => colorMapFor(rows), [rows]);

  /**
   * The caller's answer if it gave one, and otherwise the layout's.
   *
   * Vertical counts as filling: the list is under the ring with the rest of
   * the card to itself, which is exactly the case a capped list gets wrong
   * — it stops short and leaves the card's floor as paper.
   */
  const fill = legendFill ?? (beside || Boolean(vertical));

  /**
   * Changes when the LIST does, and only then.
   *
   * Built from the names rather than the values, so the fifteen-second poll
   * that refreshes the amounts does not remount anything — the rows only
   * play their entrance when the reader has actually changed what is being
   * listed, which is drilling into a group or coming back out of one.
   */
  const listKey = rows.map((slice) => slice.x).join("|");

  const ring = (
    <RingBox
      ref={ringBox}
      $vertical={vertical}
      $size={ringSize}
      /* Not when it is vertical BY CHOICE: that case sets its own width
         just above, and the wrapped ceiling was overriding it. */
      $wrapped={!vertical && !beside}
      $compact={!showTotal}
      /* Only side by side. Over the list the size is the HEIGHT and the
         aspect ratio derives the width, so an inline max-width would squash
         the ring rather than size it. */
      style={ringSize && !vertical ? { maxWidth: ringSize } : undefined}
    >
      {slices.length === 0 ? (
        <Track $inner={hole} aria-hidden="true" />
      ) : ticks ? (
        <Ticks $inner={hole} aria-hidden="true" />
      ) : null}
      {slices.length === 0 ? null : (
        <PieRing
          data={slices.map((slice) => ({ id: slice.x, value: slice.y }))}
          colorOf={colorOf}
          inner={hole}
          onSelect={onSelect}
          format={(value) => money(value, symbol, suffix)}
          /* Cash and the brokerage balance — see theme/positionTypes. The hatch is a second channel beside the
             colour, one that survives greyscale and print. */
          hatched={isHatchedType}
          /* No figure on the arcs. The share is printed once, in the
             legend's own column beside the name it belongs to. */
        />
      )}
      {showTotal && (
        <Center key={listKey}>
          {/* The figure, then its caption — DESIGN.md's Caption-Not-Heading
              rule, which this had backwards: small text above a number reads
              as a heading the number answers to, when the number is the
              thing and the words are its label. */}
          <CenterAmount>
            {centerValue ?? money(total, symbol, suffix)}
          </CenterAmount>
          <CenterCaption>{subTitle}</CenterCaption>
        </Center>
      )}
    </RingBox>
  );

  return (
    <Wrap $vertical={vertical}>
      {ring}
      <LegendBox
        ref={legendBox}
        $fill={fill}
        $vertical={vertical}
        /* The chevron is reserved whether or not this level has one — see
             the slot below. */
        $floor={legendFloor(showAmount, true)}
      >
        {/* As many rows as the card has room for, and the rest a scroll
            away. */}
        {/* No cap unless a caller insists on one: the pane takes the
              height it is given and scrolls what will not fit. */}
        <ScrollArea maxHeight={fill ? undefined : legendMaxHeight}>
          <Legend key={listKey} $fill={fill}>
            {/* EVERY row, not only the drawn ones. The arcs above are the
            positive slices — an empty holding has no arc to draw — but the
            list is the card's inventory, and dropping a row from it made a
            holding disappear from this view while the bar view of the same
            data still showed it. A brokerage account holding nothing is a
            fact about the account, not an absence of one: it belongs on the
            list at $0. */}
            {rows.map((slice, index) => {
              const empty = (slice?.y ?? 0) <= 0;
              /**
               * A row worth nothing does not open.
               *
               * It used to: the argument was that "no trading positions"
               * is a fact about the account and a row that does nothing
               * when clicked is worse than one that answers. In practice
               * three of the five groups are usually at zero, so most of
               * the list was a control that led to an empty state — and it
               * still looked like a control, with a chevron and a hover.
               * A target that resolves to nothing is worse than no target:
               * the reader learns the row is a dead end only by pressing
               * it. The chevron goes with the click, and its slot stays so
               * the columns hold.
               */
              const openable = Boolean(onSelect) && !empty;
              return (
                <Row
                  /* Position AND name. Keyed by name alone, two rows called
                     the same thing collided, and React then reconciled the
                     NEXT list against them — which is how a card that had
                     drilled into a group and come back was still showing the
                     group's rows under the whole portfolio's total. */
                  key={`${slice.x}-${index}`}
                  /* Dealt in order. Inline because the delay is per ROW
                       and a styled-component would mint a class for each
                       one. */
                  style={{ animationDelay: `${index * 22}ms` }}
                  $clickable={openable}
                  role={openable ? "button" : undefined}
                  tabIndex={openable ? 0 : undefined}
                  onClick={openable ? () => onSelect(slice.x) : undefined}
                  onKeyDown={
                    openable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onSelect(slice.x);
                          }
                        }
                      : undefined
                  }
                >
                  <Dot
                    $color={colorOf(slice.x)}
                    $empty={empty}
                    $hatched={!empty && isHatchedType(slice.x)}
                  />
                  <Names>
                    <Name>{slice.x}</Name>
                    {/* Its share of the total, worked out from the same values
                    the ring is drawn from. Guarded: a list that is all
                    zeroes has no total to take a share of.

                    It sits UNDER the name only when there is an amount at
                    the end of the row to hold the right edge. With no amount
                    it takes that edge itself, so the shares line up in a
                    column the eye can run down rather than hanging below
                    each name. */}
                  </Names>
                  {/* The share always has a column; the amount takes one
                        beside it when there is an amount to show. On a ring
                        of weights the two are the same number, so only the
                        share is printed. */}
                  <ShareColumn>
                    {total > 0 ? ((slice.y / total) * 100).toFixed(1) : "0.0"}%
                  </ShareColumn>
                  {showAmount && (
                    <Amount>{money(slice.y ?? 0, symbol, suffix)}</Amount>
                  )}
                  {/* The SLOT whenever this list opens onto anything, so
                        every row's amount ends on the same edge; the glyph
                        only on the rows that actually open. */}
                  {onSelect && (
                    <Chevron aria-hidden="true">
                      {openable ? (
                        <HugeIcon
                          icon={ArrowRight01Icon}
                          size={CHEVRON_WIDTH}
                        />
                      ) : null}
                    </Chevron>
                  )}
                </Row>
              );
            })}
          </Legend>
        </ScrollArea>
      </LegendBox>
    </Wrap>
  );
};

export default React.memo(Donut);
